import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef } from "react";

import { VoiceInput } from "../../..";

interface ChatInputProps {
  inputValue: string;
  setInputValue: (value: string) => void;
  onSend: () => void;
  onVoiceTranscript: (transcript: string) => void;
  disabled?: boolean;
  sendDisabled?: boolean;
  placeholder?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  inputValue,
  setInputValue,
  onSend,
  onVoiceTranscript,
  disabled = false,
  sendDisabled = false,
  placeholder = "Describe what you want to learn from this report and your preferred format (e.g. HTML). Include specific topics, comparisons, trends, or questions you want answered.",
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      // Set height to scrollHeight, with a max of 120px (about 5 lines)
      const maxHeight = 120;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
      // Enable scrolling if content exceeds max height
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    }
  }, [inputValue]);

  return (
    <>
      <div
        data-testid="chat-input-container"
        className="content-stretch flex min-w-0 flex-col gap-[21px] items-center justify-center relative shrink-0 w-full max-w-[842px]"
      >
        <div
          className="backdrop-blur-[20px] backdrop-filter min-w-0 max-w-[842px] relative rounded-md shrink-0 w-full min-h-[92px]"
          style={{
            backgroundColor: disabled ? "#2a2a2c" : "#1d1d1f",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <div className="flex min-w-0 flex-col justify-center max-w-inherit rounded-[inherit] size-full overflow-hidden">
            <div className="box-border content-stretch flex min-w-0 flex-col gap-2 items-start justify-center max-w-inherit p-2 pb-4 relative w-full min-h-[92px]">
              {/* Input field */}
              <div className="relative min-w-0 shrink-0 w-full">
                <div className="flex min-w-0 flex-row items-start rounded-[inherit] size-full overflow-hidden">
                  <div className="box-border content-stretch flex min-w-0 items-start px-2 py-1 relative w-full">
                    <textarea
                      ref={textareaRef}
                      data-testid="chat-input-textarea"
                      value={inputValue}
                      onChange={(e) => {
                        if (!disabled) {
                          setInputValue(e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (e.shiftKey) {
                            // Shift + Enter creates a new line (default behavior)
                            return;
                          }
                          // Enter without Shift sends the message
                          if (!disabled && !sendDisabled && inputValue.trim()) {
                            e.preventDefault();
                            onSend();
                          } else {
                            // Prevent empty new lines when can't send
                            e.preventDefault();
                          }
                        }
                      }}
                      placeholder={placeholder}
                      disabled={disabled}
                      rows={1}
                      className={`font-medium leading-5 not-italic text-base bg-transparent border-none outline-none w-full resize-none focus:outline-none focus:ring-0 ${disabled ? "text-gray-400" : "text-[#f7f7f9]"}`}
                      style={{ minHeight: "24px" }}
                    />
                  </div>
                </div>
              </div>

              {/* Buttons row */}
              <div className="flex min-w-0 items-center justify-between w-full">
                <div className="content-center flex min-w-0 flex-wrap gap-2 items-center relative shrink ml-auto">
                  {/* Shift + Return hint - only show when typing */}
                  {inputValue.trim() && (
                    <span className="text-xs text-gray-500 mr-2">
                      Shift + Return to add a new line
                    </span>
                  )}
                  <VoiceInput
                    onTranscript={onVoiceTranscript}
                    disabled={disabled}
                    size="md"
                  />
                  <button
                    onClick={onSend}
                    disabled={disabled || sendDisabled || !inputValue.trim()}
                    className="bg-orange-500 box-border content-stretch flex gap-1 items-center justify-center min-h-7 min-w-7 p-1 relative rounded-full shrink-0 hover:opacity-90 transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="chat-input-send-button"
                  >
                    <div className="relative shrink-0 size-6">
                      <ArrowLeftIcon className="w-6 h-6 rotate-90 text-white" />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            aria-hidden="true"
            className="absolute border-[0.5px] border-solid inset-0 pointer-events-none rounded-md shadow-[0px_4px_10px_0px_rgba(0,0,0,0.1)] border-white/10"
          />
        </div>
      </div>
    </>
  );
};
